import { Component, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { LinkButtonTelekomPT } from '@passthrough/components/link-btn';
import { AuthService } from '@core/services/auth.service';
import { BannerApiService } from '@core/services/banner-api.service';
import { LanguageService } from '@core/services/language.service';
import { marker, TranslationKey } from '@core/utils/marker';
import { SHARED_URLS, UserPermissions } from '@e-check/shared';

const NAV_ITEMS: { title: TranslationKey; path: string; permission?: UserPermissions }[] = [
  {
    title: marker('ROUTES.SERVICE_SCHEDULE'),
    path: '/service-schedule',
    permission: 'UI.TechnicianServiceSchedule',
  },
  {
    title: marker('ROUTES.CALL_IN_REQS'),
    path: '/call-in-reqs',
    permission: 'UI.TechnicianCallInReqs',
  },
  {
    title: marker('ROUTES.MY_APPOINTMENTS'),
    path: '/my-appointments',
    permission: 'UI.EmployeeAppointments',
  },
];

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  imports: [RouterLink, PopoverModule, ButtonModule, TranslateModule],
})
export class Navbar {
  protected readonly linkBtnPT = LinkButtonTelekomPT;
  private router = inject(Router);
  private authService = inject(AuthService);
  private bannerApiService = inject(BannerApiService);
  private langService = inject(LanguageService);

  protected user = this.authService.activeAccount;
  protected readonly urls = SHARED_URLS;

  protected maintenanceBanner = computed(
    () =>
      this.bannerApiService
        .rawBanners()
        .find((b) => b.translationKey === 'NAVBAR.MAINTENANCE.TEXT') ?? null,
  );

  protected greetingKey = computed(() => {
    this.user(); // track so greeting updates on account change
    const hour = new Date().getHours();
    if (hour < 12) return marker('NAVBAR.GREETING.MORNING');
    if (hour < 18) return marker('NAVBAR.GREETING.AFTERNOON');
    return marker('NAVBAR.GREETING.EVENING');
  });

  menuOpen = signal(false);

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  routes = computed(() => {
    let url = this.currentUrl();
    url = url.indexOf('?') > -1 ? url.slice(0, url.indexOf('?')) : url;
    const perms = this.authService.permissions();
    return NAV_ITEMS.filter((r) => !r.permission || perms.includes(r.permission)).map((r) => ({
      ...r,
      isActive: r.path === url,
    }));
  });

  protected readonly titleKey = computed(() => {
    const url = this.currentUrl();
    const isTechnicianPage = NAV_ITEMS.some(
      (r) => r.permission?.startsWith('UI.Technician') && url.startsWith(r.path),
    );
    return isTechnicianPage ? marker('NAVBAR.TITLE_TECHNICIAN') : marker('NAVBAR.TITLE');
  });

  constructor() {
    // Close the mobile menu whenever navigation completes
    effect(() => {
      this.currentUrl();
      this.menuOpen.set(false);
    });
  }

  toggleMenu() {
    this.menuOpen.update((v) => !v);
  }

  switchLanguage() {
    this.langService.toggle();
  }

  logout() {
    this.authService.logout();
  }
}
